/**
 * Shared POV bar + dimension loading for EPBCS budgeting forms.
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";

export function useDims() {
  const [dims, setDims] = useState(null);
  useEffect(() => {
    api.get("/epbcs/dimensions").then((r) => {
      const g = {};
      r.data.forEach((d) => { (g[d.dim_type] = g[d.dim_type] || []).push(d); });
      setDims(g);
    });
  }, []);
  return dims;
}

export function defaultPov(dims, keys) {
  const first = (t) => dims[t]?.[0]?.code || "";
  const map = { entity: "entity", division: "division",
    line_of_business: "line_of_business", department: "department",
    currency: "currency", version: "version" };
  const pov = {};
  keys.forEach((k) => { pov[k] = first(map[k]); });
  return pov;
}

export function PovBar({ fields, pov, setPov, testid, extra }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm shadow-sm px-4 py-2.5 flex items-center divide-x divide-slate-200 overflow-x-auto" data-testid={testid}>
      <div className="pr-4 text-[10px] uppercase font-bold tracking-[0.2em] text-slate-400 shrink-0 self-center">POV</div>
      {fields.map((f) => (
        <label key={f.k} className="flex flex-col px-4 min-w-[130px] shrink-0">
          <span className="text-[9px] uppercase font-bold tracking-[0.15em] text-slate-500 mb-0.5">{f.label}</span>
          <select value={pov[f.k] || ""} onChange={(e) => setPov({ ...pov, [f.k]: e.target.value })}
            data-testid={`${testid}-${f.k}`}
            className="text-[13px] font-semibold text-[#0B1E3F] bg-transparent border-0 p-0 pr-5 focus:ring-0 outline-none cursor-pointer hover:text-[#C8A24B] transition-colors">
            {f.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </label>
      ))}
      {extra && <div className="pl-4 ml-auto shrink-0 self-center border-0">{extra}</div>}
    </div>
  );
}

export const dimOptions = (dims, type) =>
  (dims?.[type] || []).map((d) => ({ value: d.code, label: `${d.code} — ${d.name}` }));

export const fmtMonth = (m) => {
  const names = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${names[+m.slice(5, 7) - 1]}-${m.slice(2, 4)}`;
};

export const fmtNum = (v) =>
  (v === null || v === undefined || v === "") ? "—" :
    Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 });

const SCENARIO_COLOR = {
  Actual: "bg-slate-200 text-slate-600",
  Forecast: "bg-amber-100 text-amber-700",
  Budget: "bg-emerald-100 text-emerald-700",
};

export function ScenarioTh({ m, scenarioByMonth, testid, className = "" }) {
  const sc = scenarioByMonth?.[m];
  return (
    <th className={`py-2 px-2 text-right min-w-[76px] whitespace-nowrap ${className}`}>
      <div>{fmtMonth(m)}</div>
      {sc && (
        <span data-testid={testid} className={`inline-block mt-0.5 text-[8px] font-bold normal-case tracking-normal px-1 rounded ${SCENARIO_COLOR[sc] || ""}`}>
          {sc}
        </span>
      )}
    </th>
  );
}

export function ExportButton({ report, pov }) {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const res = await api.post("/epbcs/export", { report, pov }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${report}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } finally { setBusy(false); }
  };
  return (
    <button onClick={download} disabled={busy} data-testid={`export-${report}`}
      className="inline-flex items-center gap-1.5 bg-white border border-slate-300 hover:border-[#1F6B4A] text-[#1F6B4A] px-3.5 py-2 text-[12px] font-semibold rounded-sm transition-colors hover:bg-green-50 disabled:opacity-50">
      <svg width="13" height="13" viewBox="0 0 256 256" fill="currentColor"><path d="M224,152v56a16,16,0,0,1-16,16H48a16,16,0,0,1-16-16V152a8,8,0,0,1,16,0v56H208V152a8,8,0,0,1,16,0Zm-101.66,5.66a8,8,0,0,0,11.32,0l40-40a8,8,0,0,0-11.32-11.32L136,132.69V40a8,8,0,0,0-16,0v92.69L93.66,106.34a8,8,0,0,0-11.32,11.32Z"/></svg>
      {busy ? "Exporting…" : "Export to Excel"}
    </button>
  );
}
